import { MembershipSummaryDto } from '../../../types';
import { ExportFilterValue, ExportKeyValue, ExportSheet } from '../../../lib/export';

/** Lead roles the stats panel always renders, even at zero. */
const ALL_LEAD_ROLES = [
  'Lead',
  'Community Lead',
  'Executive Champion',
  'HR Support Lead',
  'Adviser',
];

export interface MembershipFilterState {
  selectedCompany: string;
  selectedBrgId: number | null;
  brgNameById: Map<number, string>;
}

export function buildMembershipExportFilters(
  state: MembershipFilterState
): ExportFilterValue[] {
  return [
    { label: 'Company', value: state.selectedCompany || 'All Companies' },
    {
      label: 'BRG',
      value:
        state.selectedBrgId == null
          ? 'All BRGs'
          : state.brgNameById.get(state.selectedBrgId) ?? `BRG ${state.selectedBrgId}`,
    },
  ];
}

function buildSummary(
  data: MembershipSummaryDto,
  includeInvolvementBreakdown: boolean
): ExportKeyValue[] {
  const summary: ExportKeyValue[] = [
    { label: 'Total engaged employees (unique)', value: data.totalEngagedEmployees },
  ];

  // The page only shows the breakdown when viewing all BRGs; exporting it under
  // a single-BRG filter would be meaningless.
  if (includeInvolvementBreakdown) {
    summary.push(
      { label: 'Employees in 1 BRG', value: data.in1Brg },
      { label: 'Employees in 2 BRGs', value: data.in2Brgs },
      { label: 'Employees in 3 BRGs', value: data.in3Brgs },
      { label: 'Employees in 4 BRGs', value: data.in4Brgs },
      { label: 'Employees in 5+ BRGs', value: data.in5OrMoreBrgs }
    );
  }

  return summary;
}

/**
 * Membership Data is a chart page, so the export carries the numbers behind
 * the chart: one row per BRG plus the lead-role breakdown on a second sheet.
 */
export function buildMembershipExportSheets(
  data: MembershipSummaryDto,
  includeInvolvementBreakdown: boolean
): ExportSheet[] {
  const totalMembers = data.brgMemberships.reduce((sum, b) => sum + b.memberCount, 0);

  const membershipSheet: ExportSheet = {
    name: 'Membership by BRG',
    columns: [
      { key: 'brgName', header: 'BRG', width: 32 },
      { key: 'memberCount', header: 'Members', width: 12 },
      { key: 'shareOfTotal', header: '% of Memberships', width: 18, numFmt: '0.0%' },
    ],
    rows: [...data.brgMemberships]
      .sort((a, b) => b.memberCount - a.memberCount || a.brgName.localeCompare(b.brgName))
      .map((brg) => ({
        brgName: brg.brgName,
        memberCount: brg.memberCount,
        shareOfTotal: totalMembers > 0 ? brg.memberCount / totalMembers : 0,
      })),
    summaryTitle: 'Summary',
    summary: buildSummary(data, includeInvolvementBreakdown),
  };

  const leadRoleCounts = new Map(data.leadRoles.map((r) => [r.roleName, r.count]));
  const leadRolesSheet: ExportSheet = {
    name: 'Lead Roles',
    columns: [
      { key: 'roleName', header: 'Lead Role', width: 28 },
      { key: 'count', header: 'Employees', width: 14 },
    ],
    rows: ALL_LEAD_ROLES.map((roleName) => ({
      roleName,
      count: leadRoleCounts.get(roleName) ?? 0,
    })),
  };

  return [membershipSheet, leadRolesSheet];
}
